import React from 'react';

import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import styled from 'styled-components';
import * as Yup from 'yup';

import { useMutation } from '@apollo/react-hooks';
import Checkbox from '@do/walrus/Checkbox';
import DOLink from '@do/walrus/DOLink';
import SpinnerButton from '@do/walrus/SpinnerButton';
import Text from '@do/walrus/Text';
import TextInput from '@do/walrus/TextInput';
import { LOGIN_REDIRECT_URL } from '../constants';
import copy from '../copy';
import { LoginStatus } from '../interfaces/login';
import { VERIFY_DEVICE } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import {
  validationCopy,
  yupVerificationNumberValidation
} from '../utils/validation-utils';
import { ClickLink, ErrorBanner } from './Common';

const VerificationValidation = Yup.object().shape({
  code: yupVerificationNumberValidation,
  trust_device: Yup.boolean()
});

const VerificationCheckbox = styled(Checkbox)`
  && {
    text-transform: initial;
    letter-spacing: initial;
    font-size: inherit;
  }
`;

interface Props {
  goBack: () => void;
}

interface VerifyDeviceGqlResponse {
  verifyDevice: {
    status: LoginStatus;
    session_data: string;
    redirect_url: string;
  };
}

interface VerifyDeviceGqlVariables {
  verifyDeviceRequest: {
    code: string;
    session_data: string;
    trust_device: boolean;
  };
}

const VerificationForm: React.FC<Props> = ({ goBack }) => {
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [redirecting, setRedirecting] = React.useState(false);
  const [verifyDevice, { loading }] = useMutation<
    VerifyDeviceGqlResponse,
    VerifyDeviceGqlVariables
  >(VERIFY_DEVICE);

  return (
    <>
      <h4>{copy.form.verification.title}</h4>
      <Text>{copy.form.verification.details}</Text>

      <Formik
        initialValues={{
          code: '',
          trust_device: false
        }}
        validationSchema={VerificationValidation}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          verifyDevice({
            variables: {
              verifyDeviceRequest: addSessionData(values)
            }
          })
            .then((successResponse) => {
              const { status, redirect_url, session_data } = _get(
                successResponse,
                'data.verifyDevice'
              );

              saveSessionData(session_data);

              if (status === 'granted') {
                setRedirecting(true);
                window.location.assign(redirect_url || LOGIN_REDIRECT_URL);
              } else if (status === 'expired') {
                setGenericServerError(copy.errors.expired_verification_code);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.invalid_verification_code);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched }) => (
          <Form>
            {genericServerError && <ErrorBanner error={genericServerError} />}
            <TextInput
              className="do-u-mb--small"
              label={validationCopy.verification.code_label}
              placeholder={`${copy.form.two_factor_auth.placeholder_prefix} ${validationCopy.verification.code_label}`}
              name="code"
              autoFocus
              required
              touched={touched.code}
              error={errors.code}
              value={values.code}
            />
            <VerificationCheckbox
              name="trust_device"
              label={copy.form.verification.checkbox_label}
              checked={values.trust_device}
              error={errors.trust_device}
            />
            <SpinnerButton
              className="do-u-mt--medium"
              type="submit"
              fullWidth
              spinning={loading || redirecting}
              disabled={loading || redirecting}
              loadingMessage="Verifying your device"
            >
              {copy.form.verification.button_text}
            </SpinnerButton>
          </Form>
        )}
      </Formik>
      <Text type="h5" className="do-u-mb--small">
        {copy.form.verification.need_help_text}
      </Text>
      <Text className="do-u-mb--small">
        {copy.form.verification.checkout_text}{' '}
        <DOLink path="/docs/getting-started/faq/#dve">
          {copy.form.verification.troubleshooting_link}
        </DOLink>
        <> {copy.form.or} </>
        <ClickLink onClick={goBack}>
          {copy.form.verification.try_again_link}
        </ClickLink>
      </Text>
    </>
  );
};

export default VerificationForm;
